import { MinusIcon, PlusIcon } from '@heroicons/react/20/solid';
import { HeartIcon, TrashIcon } from '@heroicons/react/24/outline';
import { matchCartItemVariationsToProductVariation } from '@util/firestore/cart/cart.service';
import {
  addProductToFavorites,
  removeProductFromFavorites,
} from '@util/firestore/products';
import { trackUserInteraction } from '@util/firestore/recommendations';
import { cn, formatCurrency } from '@util/index';
import { logError } from '@util/logError';
import { getProductSlug } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { useToastContext } from 'context/ToastContext';
import { motion } from 'framer-motion';
import { ProductDocument } from 'models/product';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

interface CardProductProps {
  product: ProductDocument;
  sidebar?: boolean;
}

export default function CartProduct({ product, sidebar }: CardProductProps) {
  const {
    cart,
    increaseCartQty,
    decreaseCartQty,
    setCartOpen,
    removeFromCart,
  } = useShoppingCart();
  const { userDoc, refreshUserDoc } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();
  // used to instantly mark the product as favorite when the heart button is clicked
  const [isFavorite, setIsFavorite] = useState(false);

  const getCartItem = (productId: string) => {
    return cart?.items.find((i) => i.product_id === productId);
  };

  const cartItem = getCartItem(product.id);

  if (!product || !cartItem) return <></>;

  const variants = matchCartItemVariationsToProductVariation({
    cartItem,
    product,
  });

  const handleIncrease = () => {
    const canIncrease = cartItem !== undefined;
    if (canIncrease) {
      try {
        increaseCartQty(cartItem, variants);
      } catch (error) {
        showErrorToast('Failed to increase quantity');
        logError(error);
      }
    }
  };

  const handleDecrease = () => {
    try {
      decreaseCartQty(product.id);
    } catch (error) {
      showErrorToast('Failed to decrease quantity');
      logError(error);
    }
  };

  const handleFavorite = async () => {
    if (!userDoc?.uid) return;
    try {
      if (userDoc?.favorites?.includes(product.id)) {
        setIsFavorite(false);
        await removeProductFromFavorites(userDoc.uid, product.id);
        showSuccessToast('Removed from favorites');
        await trackUserInteraction({
          uid: userDoc.uid,
          pid: product.id,
          interaction: 'unfavorite',
        });
      } else {
        setIsFavorite(true);
        await addProductToFavorites(userDoc.uid, product.id);
        showSuccessToast('Added to favorites');
        await trackUserInteraction({
          uid: userDoc.uid,
          pid: product.id,
          interaction: 'favorite',
        });
      }
      refreshUserDoc(userDoc.uid);
    } catch (error) {
      showErrorToast('Failed to add to favorites');
      logError(error);
    }
  };

  const imageSource = variants?.[0]?.image || product.thumbnail;

  return (
    <div className="flex flex-col gap-y-4 p-8">
      <div className="flex gap-x-4">
        <Link href={getProductSlug(product)} onClick={() => setCartOpen(false)}>
          <Image
            src={imageSource}
            alt={product.title}
            className={`aspect-square h-[14rem] w-[16rem] ${
              imageSource.includes('wpsstatic') ||
              product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
                ? 'object-contain'
                : 'object-cover'
            }`}
            height={480}
            width={480}
          />
        </Link>

        <div className="flex w-full justify-between">
          <div className="flex w-full flex-col justify-between gap-y-2">
            <div className="flex flex-col gap-y-4">
              <div
                className={`flex w-full items-start ${
                  sidebar ? 'flex-col gap-y-4' : 'justify-between gap-[5rem]'
                }`}
              >
                <div
                  className={`flex flex-col gap-y-2 ${
                    sidebar ? 'w-full' : 'w-[40rem]'
                  }`}
                >
                  <div className="flex w-full items-start justify-between">
                    <Link
                      href={getProductSlug(product)}
                      onClick={() => setCartOpen(false)}
                      className="line-clamp-2 text-[1.8rem] font-medium text-black"
                    >
                      {product.title}
                    </Link>

                    {sidebar && (
                      <div className="h-full min-w-[12rem] items-end justify-between">
                        <p
                          data-on-sale={product.on_sale}
                          className="text-right text-[1.8rem] font-semibold text-black data-[on-sale=true]:text-brand-secondary"
                        >
                          {formatCurrency(cartItem.product_cost)}
                        </p>

                        <div className="mt-[1rem] flex justify-end">
                          <div className="flex items-end gap-4">
                            <motion.button
                              type="button"
                              className="flex items-center justify-center"
                              onClick={handleFavorite}
                              whileTap={{ scale: 0.95 }}
                            >
                              <HeartIcon
                                className={cn(
                                  userDoc?.favorites?.includes(product.id) ||
                                    isFavorite
                                    ? 'fill-brand-secondary text-brand-secondary'
                                    : 'text-black transition-colors duration-150 ease-linear hover:text-brand-secondary',
                                  'h-10 w-10'
                                )}
                              />
                            </motion.button>
                            <button
                              type="button"
                              className="flex items-center justify-center"
                              onClick={() => removeFromCart(product.id)}
                            >
                              <TrashIcon className="size-9 text-black transition-colors duration-150 ease-linear hover:text-brand-secondary" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <p className="text-[1.6rem] text-[#727272]">
                    {product.category === 'Bike Parts' &&
                      (product.category2 ||
                        product.category1 ||
                        product.category)}
                    {product.category === 'Riding Gear' &&
                      [
                        cartItem?.size?.letter || product.size?.letter,
                        cartItem?.size?.number || product.size?.number,
                        cartItem?.color,
                        ...Object.values(cartItem?.custom_inputs?.values ?? {}),
                      ]
                        .filter(Boolean)
                        .join(' | ')}
                  </p>

                  {product.category !== 'Riding Gear' &&
                    (cartItem?.size?.letter ||
                      cartItem?.size?.number ||
                      product.size?.letter ||
                      product.size?.number) && (
                      <p className="text-[1.6rem] text-[#727272]">
                        Size:{' '}
                        {cartItem?.size?.letter ||
                          product.size?.letter ||
                          cartItem?.size?.number ||
                          product.size?.number}
                      </p>
                    )}

                  {!sidebar && (
                    <div className="flex items-center gap-4">
                      <motion.button
                        type="button"
                        className="flex items-center justify-center"
                        onClick={handleFavorite}
                        whileTap={{ scale: 0.95 }}
                      >
                        <HeartIcon
                          className={cn(
                            userDoc?.favorites?.includes(product.id) ||
                              isFavorite
                              ? 'fill-brand-secondary text-brand-secondary'
                              : 'text-black transition-colors duration-150 ease-linear hover:text-brand-secondary',
                            'h-10 w-10'
                          )}
                        />
                      </motion.button>
                      <button
                        type="button"
                        className="flex items-center justify-center"
                        onClick={() => removeFromCart(product.id)}
                      >
                        <TrashIcon className="size-9 text-black transition-colors duration-150 ease-linear hover:text-brand-secondary" />
                      </button>
                    </div>
                  )}

                  {sidebar && (
                    <div className="flex w-fit items-center gap-x-[2rem] rounded-[0.5rem] border-[1px] border-gray-400 px-[1rem] py-[1rem] font-semibold">
                      <button
                        type="button"
                        className="flex items-center justify-center"
                        onClick={handleDecrease}
                      >
                        <MinusIcon className="h-[2rem] w-[2rem]" />
                      </button>
                      {cartItem.qty}
                      <button
                        type="button"
                        className="flex items-center justify-center"
                        onClick={handleIncrease}
                      >
                        <PlusIcon className="h-[2rem] w-[2rem]" />
                      </button>
                    </div>
                  )}
                </div>

                {!sidebar && (
                  <div className="flex min-w-[30rem] items-center justify-between">
                    <div className="flex flex-col items-center gap-y-4">
                      <div
                        className={`flex w-fit items-center gap-x-[2rem] rounded-[0.5rem] border-[1px] border-gray-400 px-[1rem] py-[1rem] font-semibold`}
                      >
                        <button
                          type="button"
                          className="flex items-center justify-center"
                          onClick={handleDecrease}
                        >
                          <MinusIcon className="h-[2rem] w-[2rem]" />
                        </button>
                        {cartItem.qty}
                        <button
                          type="button"
                          className="flex items-center justify-center"
                          onClick={handleIncrease}
                        >
                          <PlusIcon className="h-[2rem] w-[2rem]" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col items-end justify-center gap-2">
                      <div className="flex items-center gap-x-2">
                        {product.on_sale && (
                          <span className="rounded-[1rem] bg-brand-secondary px-4 py-2 text-[1.6rem] font-semibold text-white">
                            {Math.round(
                              ((product.og_price -
                                (variants?.[0]?.price || product.price)) /
                                product.og_price) *
                                100
                            )}
                            % Off
                          </span>
                        )}

                        <p
                          data-on-sale={product.on_sale}
                          className="text-right text-[2rem] font-semibold text-black"
                        >
                          {formatCurrency(cartItem.product_cost * cartItem.qty)}
                        </p>
                      </div>

                      {product.on_sale && (
                        <p
                          data-on-sale={product.on_sale}
                          className="text-right text-[1.6rem] font-semibold text-zinc-500"
                        >
                          List Price:{' '}
                          <span className="line-through">
                            {formatCurrency(product.og_price)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
